import { Link, Navigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/use-auth';
import { CalendarDays, CheckCircle2, ChevronRight, Lock } from 'lucide-react';
import { useLabels } from '../config/useLabels';
import { eligibleStudents, isRoundOpen, type EligibilityCandidate } from '../utils/signupEligibility';
import { formatTimeOfDay, formatWeekdayDateDisplay, getTodayDateStringInBusinessTZ } from '../utils/date';
import { describeLockWindow } from '../utils/signupLock';

interface SignupEvent {
  id: string;
  title: string;
  description: string | null;
  start_date: string;
  end_date: string;
  slot_duration_minutes: number;
  max_signups_per_slot: number;
  is_active: boolean;
  created_at: string;
  target_grades: string[] | null;
  lock_hours_before?: number | null;
}

/** One child's slot in one round, as the card names it. */
interface FamilyBooking {
  eventId: string;
  studentId: string;
  slotDate: string;
  startTime: string;
}

export default function SignupEvents() {
  const { user, isLoading: authLoading } = useAuth();

  // Early return if auth is loading
  if (authLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-2 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Early return if not authenticated
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <SignupEventsContent />;
}

function SignupEventsContent() {
  const t = useLabels();
  const { user } = useAuth();
  const userEmail = user?.email;

  // Fetch all active signup events
  const { data: events = [], isLoading: eventsLoading } = useQuery({
    queryKey: ['signup-events'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('signup_events')
        .select('*')
        .eq('is_active', true)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as SignupEvent[];
    },
  });

  // This page is a destination now, not just a redirect target, so it has to apply the same
  // audience rule the dashboard does. Listing a round a family cannot book is worse than not
  // listing it: they click through and find an empty student picker with no explanation.
  const { data: family, isLoading: familyLoading } = useQuery({
    queryKey: ['signup-family', userEmail],
    queryFn: async () => {
      if (!userEmail) return { candidates: [] as EligibilityCandidate[], bookings: [] as FamilyBooking[] };

      const { data: parentRow } = await supabase
        .from('parents').select('id').eq('email', userEmail).single();
      if (!parentRow) return { candidates: [] as EligibilityCandidate[], bookings: [] as FamilyBooking[] };

      const { data: links } = await supabase
        .from('student_parents')
        .select('students!inner (id, first_name, active, metadata)')
        .eq('parent_id', parentRow.id);

      type StudentRow = { id: string; first_name: string; active: boolean; metadata: { grade?: string | null } | null };
      const students = (links ?? []).flatMap((row: { students: StudentRow | StudentRow[] }) =>
        Array.isArray(row.students) ? row.students : [row.students]
      ).filter(Boolean);
      const studentIds = students.map(s => s.id);

      const byStudent: Record<string, string[]> = {};
      const bookings: FamilyBooking[] = [];
      if (studentIds.length > 0) {
        const { data: enrollmentRows } = await supabase
          .from('enrollments')
          .select('student_id, programs!inner (type, active)')
          .in('student_id', studentIds)
          .eq('is_active', true);

        type ProgramRef = { type: string | null; active: boolean };
        for (const row of (enrollmentRows ?? []) as unknown as { student_id: string; programs: ProgramRef | ProgramRef[] | null }[]) {
          const programs = Array.isArray(row.programs) ? row.programs : row.programs ? [row.programs] : [];
          for (const program of programs) {
            if (program.active && program.type) (byStudent[row.student_id] ??= []).push(program.type);
          }
        }

        const { data: registrations } = await supabase
          .from('signup_registrations')
          .select('student_id, signup_slots!inner (event_id, slot_date, start_time)')
          .in('student_id', studentIds);

        type SlotRef = { event_id: string; slot_date: string; start_time: string };
        for (const row of (registrations ?? []) as unknown as { student_id: string | null; signup_slots: SlotRef | SlotRef[] | null }[]) {
          const slot = Array.isArray(row.signup_slots) ? row.signup_slots[0] : row.signup_slots;
          if (slot && row.student_id) {
            bookings.push({ eventId: slot.event_id, studentId: row.student_id, slotDate: slot.slot_date, startTime: slot.start_time });
          }
        }
      }

      return {
        candidates: students.map(student => ({
          id: student.id,
          firstName: student.first_name,
          active: student.active,
          grade: student.metadata?.grade ?? null,
          activeProgramTypes: byStudent[student.id] ?? [],
        })) as EligibilityCandidate[],
        bookings,
      };
    },
    enabled: !!userEmail,
  });

  const today = getTodayDateStringInBusinessTZ();
  const firstNameById = new Map((family?.candidates ?? []).map(c => [c.id, c.firstName]));

  // A round that has ended stays active until somebody switches it off; the dashboard already
  // stops pointing at it, and so does this list.
  const rounds = events
    .filter(event => isRoundOpen({ id: event.id, title: event.title, endDate: event.end_date }, today))
    .map(event => {
      const eligible = eligibleStudents(family?.candidates ?? [], {
        id: event.id,
        title: event.title,
        endDate: event.end_date,
        targetGrades: event.target_grades,
      });
      const bookings = (family?.bookings ?? [])
        .filter(booking => booking.eventId === event.id)
        .sort((a, b) => a.slotDate.localeCompare(b.slotDate) || a.startTime.localeCompare(b.startTime));
      const bookedIds = new Set(bookings.map(booking => booking.studentId));
      return { event, eligible, bookings, unbooked: eligible.filter(student => !bookedIds.has(student.id)) };
    })
    .filter(round => round.eligible.length > 0);

  if (eventsLoading || familyLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-2 text-gray-600">Loading signup events...</p>
        </div>
      </div>
    );
  }

  if (rounds.length === 0) {
    return (
      <div className="px-4 py-12 text-center">
        <h1 className="text-2xl font-semibold text-gray-900 mb-4">No Active Events</h1>
        <p className="text-gray-600">There are no active signup events available at this time.</p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl space-y-5 px-4 pb-6 sm:px-0">
      <div>
        <h1 className="text-xl font-semibold text-gray-900 sm:text-2xl">{t('action.signUpEvents')}</h1>
        <p className="mt-1 text-sm text-gray-600">Pick a round to book, change or cancel a meeting time.</p>
      </div>

      <ul className="space-y-3">
        {rounds.map(({ event, bookings, unbooked }) => (
          <li key={event.id}>
            <Link
              to={`/signup/${event.id}`}
              className="block rounded-xl border border-gray-200 bg-white p-4 transition-colors hover:border-blue-300 hover:shadow-sm sm:p-5"
            >
              <div className="flex items-start justify-between gap-3">
                <h2 className="text-base font-semibold leading-snug text-gray-900 sm:text-lg">{event.title}</h2>
                <ChevronRight className="mt-0.5 h-5 w-5 flex-shrink-0 text-gray-400" aria-hidden />
              </div>

              <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-gray-500">
                <span className="inline-flex items-center gap-1">
                  <CalendarDays className="h-4 w-4" aria-hidden />
                  {formatWeekdayDateDisplay(event.start_date)} – {formatWeekdayDateDisplay(event.end_date)}
                </span>
                {event.lock_hours_before != null && (
                  <span className="inline-flex items-center gap-1">
                    <Lock className="h-4 w-4" aria-hidden />
                    Changes close {describeLockWindow(event.lock_hours_before)} before
                  </span>
                )}
              </div>

              {event.description && (
                <p className="mt-2 line-clamp-2 whitespace-pre-line text-sm text-gray-600">{event.description}</p>
              )}

              {(bookings.length > 0 || unbooked.length > 0) && (
                <ul className="mt-3 space-y-1 text-sm">
                  {bookings.map(booking => (
                    <li key={booking.studentId} className="flex items-center gap-1.5 text-green-800">
                      <CheckCircle2 className="h-4 w-4 flex-shrink-0" aria-hidden />
                      <span>
                        <span className="font-medium">{firstNameById.get(booking.studentId) ?? 'Booked'}</span>
                        {' · '}
                        <span className="tabular-nums">
                          {formatWeekdayDateDisplay(booking.slotDate)}, {formatTimeOfDay(booking.startTime, { hour12: true })}
                        </span>
                      </span>
                    </li>
                  ))}
                  {unbooked.length > 0 && (
                    <li className="text-amber-700">
                      Not booked yet: {unbooked.map(student => student.firstName).join(', ')}
                    </li>
                  )}
                </ul>
              )}

              <span
                className={`mt-3 inline-flex items-center rounded-full px-3 py-1 text-sm font-medium ${
                  unbooked.length > 0
                    ? 'bg-blue-600 text-white'
                    : 'border border-gray-300 bg-white text-gray-700'
                }`}
              >
                {unbooked.length > 0 ? 'Book a time' : 'Change or cancel'}
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
